import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Heading,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { AddCreditFacilityButton } from './AddCreditFacilityButton';
import { UpdateCreditFacilityIsActiveStatusButton } from './UpdateCreditFacilityIsActiveStatusButton';
import { UpdateCreditFacilityNameButton } from './UpdateCreditFacilityNameButton';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { GetOrganizationImplementationsDetailsQueryGQLType } from '~/packages/implementations/requests/implementations.gql.generated';

// Types
export interface CreditFacility {
  id: string;
  name: string;
  slug: string;
  borrowerEntityName: string;
  type: string;
  isActive: boolean;
}

// Type alias for implementation from the client query
type OrganizationImplementationFragmentGQLType = GetOrganizationImplementationsDetailsQueryGQLType['organization']['implementations'][0];

// Component for credit facility action buttons
const CreditFacilityActionButtons = ({
  implementation,
  creditFacility,
}: {
  implementation: OrganizationImplementationFragmentGQLType;
  creditFacility: CreditFacility;
}) => (
  <>
    <UpdateCreditFacilityNameButton
      implementation={implementation}
      creditFacility={creditFacility}
    />
    <UpdateCreditFacilityIsActiveStatusButton
      implementation={implementation}
      creditFacility={creditFacility}
    />
  </>
);

export interface CreditFacilitiesTableProps<
  TImplementation extends OrganizationImplementationFragmentGQLType = OrganizationImplementationFragmentGQLType
> {
  implementation: TImplementation;
  implementationRoutePrefix: string; // e.g., `/organizations/{orgId}/implementations/`
  organizations: Array<{ id: string; name: string; slug: string }>;
}

// Exports
export const CreditFacilitiesTable = <
  TImplementation extends OrganizationImplementationFragmentGQLType = OrganizationImplementationFragmentGQLType
>({
  implementation,
  implementationRoutePrefix,
  organizations,
}: CreditFacilitiesTableProps<TImplementation>) => {
  return (
    <Box mb="35px">
      <Flex justifyContent="space-between" padding="25px 0px">
        <Heading display="inline-block" fontSize="3xl" as="h3">
          Credit Facilities
        </Heading>
        <AddCreditFacilityButton
          implementation={implementation}
          organizations={organizations}
        />
      </Flex>
      <Table>
        <Thead>
          <Tr>
            <Th>Id</Th>
            <Th>Name</Th>
            <Th>Slug</Th>
            <Th>Borrower Entity Name</Th>
            <Th>Facility Type</Th>
            <Th>Status</Th>
            <Th />
          </Tr>
        </Thead>
        <Tbody>
          {implementation.creditFacilities.map((creditFacility) => (
            <Tr key={creditFacility.id}>
              <Td>{creditFacility.id}</Td>
              <Td>{creditFacility.name}</Td>
              <Td>{creditFacility.slug}</Td>
              <Td>{creditFacility.borrowerEntityName}</Td>
              <Td>{_.startCase(creditFacility.type)}</Td>
              <Td>{creditFacility.isActive ? 'Active' : 'Deactivated'}</Td>
              <Td>
                <Flex justifyContent="space-between">
                  <IconButtonV2
                    isDisabled={!creditFacility.isActive}
                    aria-label="View credit agreements"
                    label={
                      !creditFacility.isActive
                        ? 'Cannot view disabled credit facilities'
                        : 'View credit agreements'
                    }
                    icon={<Icon icon="file-lines" />}
                    variant="v2-ghost"
                    colorScheme="gray"
                    IconButtonProps={
                      creditFacility.isActive
                        ? {
                            as: RouterLink,
                            to: `${implementationRoutePrefix}${implementation.id}/credit-facilities/${creditFacility.id}/credit-agreements`,
                          }
                        : undefined
                    }
                  />
                  <CreditFacilityActionButtons
                    implementation={implementation}
                    creditFacility={creditFacility}
                  />
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      {implementation.creditFacilities.length === 0 && (
        <Alert>
          <AlertIcon />
          <AlertDescription>No credit facilities configured</AlertDescription>
        </Alert>
      )}
    </Box>
  );
};
